import React from 'react';
import { IngredientsList } from '../../components/inventory/IngredientsList';
import type { InventoryItem } from '../../types/inventory';
import { InventoryItemDetailsModals } from './components/InventoryItemDetailsModals';
import { InventoryItemTabs } from './components/InventoryItemTabs';
import { InventoryLotsSection } from './components/InventoryLotsSection';
import { InventorySamplesSection } from './components/InventorySamplesSection';
import { useInventoryItemDetailsState } from '../../hooks/inventory-hooks/useInventoryItemDetailsState';

interface InventoryItemDetailsProps {
  item: InventoryItem;
  canWrite: boolean;
}

export const InventoryItemDetails: React.FC<InventoryItemDetailsProps> = ({ item, canWrite }) => {
  const {
    activeTab,
    setActiveTab,
    lots,
    samples,
    ingredients,
    loading,
    error,
    isCreateSampleOpen,
    setIsCreateSampleOpen,
    qcModal,
    setQcModal,
    isHistoryOpen,
    setIsHistoryOpen,
    selectedLotId,
    selectedLotNumber,
    roastModalContext,
    setRoastModalContext,
    greenBeanProfiles,
    refetchCupping,
    refetchGreenBeans,
    fetchData,
    showSamplesTab,
    formatDate,
    getGreenBeanQC,
    handleQCClick,
    getInitialQCData,
    handleCreateSample,
    handleHistoryClick,
    handleHistoryUpdate,
  } = useInventoryItemDetailsState(item);

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200" data-test-id="inventory-item-details">
      <InventoryItemTabs
        itemType={item.itemType}
        activeTab={activeTab}
        showSamplesTab={showSamplesTab}
        onTabChange={setActiveTab}
      />

      {activeTab === 'lots' && (
        <div>
          <InventoryLotsSection
            lots={lots}
            loading={loading}
            error={error}
            greenBeanProfiles={greenBeanProfiles}
            formatDate={formatDate}
            onProfileUpdate={fetchData}
            onRoastClick={(referenceId, label) => setRoastModalContext({ referenceId, label })}
            getGreenBeanQC={getGreenBeanQC}
            onQCClick={handleQCClick}
            onHistoryClick={handleHistoryClick}
            canWrite={canWrite}
          />
        </div>
      )}

      {activeTab === 'ingredients' && (
        <div className="bg-white rounded-b-lg border border-gray-200 overflow-x-auto" data-test-id="inventory-ingredients-section">
          <IngredientsList
            ingredients={ingredients}
            onViewRoast={ingredient =>
              setRoastModalContext({
                referenceId: ingredient.woReferenceId || ingredient.id,
                label: `Roast Profile – Ingredient ${ingredient.name}`,
                readOnly: true,
              })
            }
          />
        </div>
      )}

      {activeTab === 'sample' && showSamplesTab && (
        <InventorySamplesSection
          samples={samples}
          onProfileUpdate={fetchData}
          onRoastClick={(referenceId, label) => setRoastModalContext({ referenceId, label })}
          getGreenBeanQC={getGreenBeanQC}
          onQCClick={handleQCClick}
          onAddSample={() => setIsCreateSampleOpen(true)}
          canWrite={canWrite}
        />
      )}

      <InventoryItemDetailsModals
        itemId={item.id}
        itemType={item.itemType}
        itemName={item.name}
        workOrderReferenceId={item.woReferenceId || item.workOrderId}
        isCreateSampleOpen={isCreateSampleOpen}
        onCloseCreateSample={() => setIsCreateSampleOpen(false)}
        onCreateSample={handleCreateSample}
        qcModal={qcModal}
        onCloseQcModal={() => setQcModal(prev => ({ ...prev, isOpen: false }))}
        getInitialQCData={getInitialQCData}
        onQcSuccess={() => {
          setQcModal(prev => ({ ...prev, isOpen: false }));
          fetchData(true);
        }}
        onRefetchGreenBeans={refetchGreenBeans}
        onRefetchCupping={refetchCupping}
        isHistoryOpen={isHistoryOpen}
        onCloseHistory={() => setIsHistoryOpen(false)}
        selectedLotId={selectedLotId}
        selectedLotNumber={selectedLotNumber}
        lots={lots}
        onHistoryUpdate={handleHistoryUpdate}
        roastModalContext={roastModalContext}
        onCloseRoastModal={() => setRoastModalContext(null)}
        onRoastSaved={() => fetchData(true)}
        canWrite={canWrite}
      />
    </div>
  );
};
